// --------------------------
// IMPORTS
// --------------------------
import { Row, Col, Form, message } from "antd";
import { useEffect, useState } from "react";
import dayjs from "dayjs";

import { useDispatch, useSelector } from "react-redux";
import DatePickerField from "../../components/atoms/DatePickerField";
import SubmitButton from "../../components/atoms/Buttons/SubmitButton";
import TopContainerHeader from "../../components/molecules/TopComponentHeader";
import {
  blockDateThunk,
  getBlockedDateThunk,
  unblockDateThunk,
} from "../../store/thunks/expenseCalendarControlsThunk";
import CustomLoader from "../../utils/CustomLoader";

// --------------------------
// MAIN COMPONENT
// --------------------------
export default function ExpenseCalendarControl() {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();

  // --------------------------
  // REDUX STATE
  // --------------------------
  const storeRole = useSelector((state) => state.auth.role);
  const storeLIUId = useSelector((state) => state.auth.lIUId);

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [selectedDate, setSelectedDate] = useState(null);
  const [showLoader, setShowLoader] = useState(false);
  const [blockedDate, setBlockedDate] = useState(null);

  // --------------------------
  // API CALLS
  // --------------------------
  const expCal = () => {
    setShowLoader(true);

    const payload = {
      role: storeRole,
      lIUId: storeLIUId,
    };

    dispatch(getBlockedDateThunk(payload))
      .unwrap()
      .then((response) => {
        if (response?.expCalRes) {
          const data = response.expCalRes;
          setBlockedDate(data);
          if (data.status === process.env.REACT_APP_BLOCK_STATUS) {
            setSelectedDate(dayjs(data.blockedOn));
          } else {
            setSelectedDate(null);
          }
        } else {
          setBlockedDate(null);
          setSelectedDate(null);
        }
      })
      .finally(() => setShowLoader(false));
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------
  useEffect(() => {
    if (blockedDate) {
      if (blockedDate.status === process.env.REACT_APP_BLOCK_STATUS) {
        setSelectedDate(dayjs(blockedDate.blockedOn));
      } else {
        setSelectedDate(null);
      }
    }
  }, [blockedDate]);

  useEffect(() => {
    expCal();
  }, []);

  // --------------------------
  // HANDLERS
  // --------------------------

  const handleBlock = () => {
    if (!selectedDate) {
      message.warning("Please select a date.");
      return;
    }

    setShowLoader(true);
    const payload = {
      blockedOn: selectedDate.format("YYYY-MM-DD"),
      historySource: "backend",
      historyAction: "block",
      historyUserID: storeLIUId,
      status: process.env.REACT_APP_BLOCK_STATUS,
    };
    dispatch(blockDateThunk(payload))
      .unwrap()
      .then((response) => {
        if (response) {
          message.success("Calendar date blocked successfully.");
          setBlockedDate({
            ...payload,
            _id: response?._id || blockedDate?._id,
          });
          setSelectedDate(null);
        }
      })
      .catch(() => {
        message.error("Failed to block calendar date.");
      })
      .finally(() => setShowLoader(false));
  };

  const handleUnblock = () => {
    if (!blockedDate) {
      message.warning("No blocked date to unblock.");
      return;
    }
    setShowLoader(true);

    const payload = {
      blockedOn: blockedDate?.blockedOn,
      historySource: "backend",
      historyAction: "unblock",
      historyUserID: storeLIUId,
      status: process.env.REACT_APP_UNBLOCK_STATUS,
    };

    dispatch(unblockDateThunk(payload))
      .unwrap()
      .then((response) => {
        if (response === process.env.REACT_APP_UNBLOCK_STATUS) {
          message.success("Calendar date unblocked successfully.");
          setBlockedDate(null);
          setSelectedDate(null);
        }
      })
      .catch(() => {
        message.error("Failed to unblock calendar date.");
      })
      .finally(() => setShowLoader(false));
  };

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <div className="container">
      <TopContainerHeader
        sectionNameClass="col-lg-7 text-left commonTitleText"
        sectionTitle="Block / Unblock Calendar Date"
        hasSectionButton={false}
        hasSearch={false}
      />
      {showLoader ? (
        <CustomLoader showLoader={showLoader} />
      ) : (
        <div className="custom-card">
          <div className="custom-card-body">
            <Form layout="vertical">
              <Row gutter={[16, 16]} align="bottom">
                <Col sm={8}>
                  <Form.Item label="" className="m-0">
                    <DatePickerField
                      selectedDate={selectedDate}
                      blockedDate={blockedDate}
                      onChange={(date) => {
                        setSelectedDate(date); // set the dayjs object directly, or null if cleared
                      }}
                    />
                  </Form.Item>
                </Col>

                {blockedDate?.status === process.env.REACT_APP_BLOCK_STATUS ? (
                  <Col sm={"auto"}>
                    <Form.Item className="m-0 cusHeight38">
                      <SubmitButton
                        ky="unblock"
                        type="danger"
                        submitName="Unblock"
                        className="btn px-5 py-2 defaultbutton rounded-lg"
                        onClick={handleUnblock}
                        loading={showLoader}
                      />
                    </Form.Item>
                  </Col>
                ) : (
                  <Col sm={"auto"}>
                    <Form.Item className="m-0 cusHeight38">
                      <SubmitButton
                        ky="block"
                        type="danger"
                        submitName="Block"
                        onClick={handleBlock}
                        loading={showLoader}
                        className="btn px-5 py-2 defaultbutton rounded-lg"
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </Form>
          </div>
        </div>
      )}
    </div>
  );
}
